import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, ExternalLink, Minus, Plus, CreditCard } from 'lucide-react';
import {
  CLOUD_PLANS,
  CLOUD_PLAN_CATEGORIES,
  findCloudPlan,
  type CheckoutResponse,
  type BillingPortalResponse,
  type CloudPlan,
  type PortalSubscription,
} from '@scada-ink/shared/cloud';
import { Button } from '@/components/ui';
import { Alert } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { useSession } from '@/lib/session';
import { useAsync } from '@/lib/useAsync';
import { Card, EmptyState, LoadError, Loading, PortalHeader, SubscriptionBadge, Table, formatDate } from './components';

const LIVE = new Set(['active', 'trialing', 'past_due', 'incomplete']);

function QuantityInput({ value, onChange, disabled }: { value: number; onChange: (n: number) => void; disabled?: boolean }) {
  return (
    <div className="inline-flex items-center rounded-lg border border-glass-border-bright bg-white">
      <button type="button" className="p-1.5 disabled:opacity-40" disabled={disabled || value <= 1} onClick={() => onChange(value - 1)} aria-label="Fewer appliances">
        <Minus size={13} />
      </button>
      <span className="w-8 text-center text-sm font-semibold tabular-nums">{value}</span>
      <button type="button" className="p-1.5 disabled:opacity-40" disabled={disabled || value >= 500} onClick={() => onChange(value + 1)} aria-label="More appliances">
        <Plus size={13} />
      </button>
    </div>
  );
}

function PlanOption({ plan, subscribed, preselected, onSubscribe, busy }: { plan: CloudPlan; subscribed: boolean; preselected: boolean; onSubscribe: (plan: CloudPlan, qty: number) => void; busy: boolean }) {
  const [qty, setQty] = useState(1);
  return (
    <div className={`glass-card p-5 flex flex-col ${preselected ? 'ring-4 ring-brand-primary/15 border-brand-primary/50' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold">{plan.name}</div>
          <div className="text-xs text-text-secondary">{plan.tagline}</div>
        </div>
        <div className="text-right whitespace-nowrap">
          <div className="text-lg font-bold">${plan.priceMonthlyUsd}</div>
          <div className="text-[11px] text-text-muted">{plan.unit === 'appliance' ? 'per appliance / mo' : 'per month'}</div>
        </div>
      </div>
      <ul className="mt-3 space-y-1.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-1.5 text-xs text-text-secondary">
            <Check size={13} className="text-status-running mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center justify-between gap-3">
        {plan.unit === 'appliance' && !subscribed ? (
          <span className="flex items-center gap-2 text-xs text-text-muted">
            <QuantityInput value={qty} onChange={setQty} disabled={busy} /> appliances
          </span>
        ) : (
          <span />
        )}
        {subscribed ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-status-running">
            <Check size={14} /> Subscribed
          </span>
        ) : (
          <Button size="sm" disabled={busy} onClick={() => onSubscribe(plan, qty)}>
            Subscribe{plan.unit === 'appliance' && qty > 1 ? ` · $${plan.priceMonthlyUsd * qty}/mo` : ''}
          </Button>
        )}
      </div>
    </div>
  );
}

export function BillingPage() {
  usePageTitle('Plans & Billing');
  const { session, refresh } = useSession();
  const [params, setParams] = useSearchParams();
  const { data: subs, error, loading, reload } = useAsync(() => cloudApi.get<PortalSubscription[]>('/api/portal/subscriptions'));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error' | 'info'; text: string } | null>(null);

  const checkout = params.get('checkout');
  const preselect = findCloudPlan(params.get('plan') ?? '');

  useEffect(() => {
    if (checkout === 'success') {
      setNotice({ kind: 'success', text: 'Payment complete. Your subscription activates as soon as Stripe confirms it — usually within a few seconds.' });
      void refresh();
      // The webhook may land a moment after the redirect.
      const t = window.setTimeout(() => void reload(), 3000);
      return () => window.clearTimeout(t);
    }
    if (checkout === 'cancel') setNotice({ kind: 'info', text: 'Checkout was canceled. You have not been charged.' });
  }, [checkout, reload, refresh]);

  useEffect(() => {
    if (preselect) document.getElementById(`plan-${preselect.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [preselect, subs]);

  const isOwner = session?.user.role === 'owner';
  const live = (subs ?? []).filter((s) => LIVE.has(s.status));
  const subscribedIds = new Set(live.map((s) => s.planId));

  async function subscribe(plan: CloudPlan, quantity: number) {
    const total = plan.priceMonthlyUsd * (plan.unit === 'appliance' ? quantity : 1);
    const provider = session?.billing.provider;
    if (provider === 'invoice' && !window.confirm(`Subscribe to ${plan.name} for $${total}/month? It activates now and is billed by invoice.`)) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await cloudApi.post<CheckoutResponse>('/api/portal/billing/checkout', { planId: plan.id, quantity });
      if (result.kind === 'redirect') {
        window.location.assign(result.url);
        return;
      }
      setNotice({ kind: 'success', text: `${plan.name} is active.` });
      params.delete('plan');
      setParams(params, { replace: true });
      await reload();
    } catch (err) {
      setNotice({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  async function cancel(sub: PortalSubscription) {
    const plan = findCloudPlan(sub.planId);
    const when = sub.provider === 'stripe' ? 'at the end of the current billing period' : 'immediately';
    if (!window.confirm(`Cancel ${plan?.name ?? sub.planId}? It will end ${when}.`)) return;
    setBusy(true);
    setNotice(null);
    try {
      await cloudApi.post(`/api/portal/subscriptions/${encodeURIComponent(sub.id)}/cancel`);
      await reload();
    } catch (err) {
      setNotice({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  async function changeQuantity(sub: PortalSubscription, quantity: number) {
    setBusy(true);
    setNotice(null);
    try {
      await cloudApi.patch(`/api/portal/subscriptions/${encodeURIComponent(sub.id)}`, { quantity });
      await reload();
    } catch (err) {
      setNotice({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  async function openPortal() {
    setBusy(true);
    try {
      const { url } = await cloudApi.post<BillingPortalResponse>('/api/portal/billing/portal');
      window.location.assign(url);
    } catch (err) {
      setNotice({ kind: 'error', text: errorMessage(err) });
      setBusy(false);
    }
  }

  const monthly = live.reduce((sum, s) => sum + (findCloudPlan(s.planId)?.priceMonthlyUsd ?? 0) * s.quantity, 0);

  return (
    <>
      <PortalHeader
        title="Plans & Billing"
        description={
          session?.billing.provider === 'stripe'
            ? 'Subscribe with a card through Stripe. Manage payment methods and invoices in the billing portal.'
            : 'Plans activate immediately and are billed monthly by invoice.'
        }
        actions={
          session?.billing.portalAvailable && (
            <Button variant="secondary" size="sm" onClick={() => void openPortal()} disabled={busy}>
              <ExternalLink size={14} /> Payment methods & invoices
            </Button>
          )
        }
      />

      {notice && (
        <div className="mb-6">
          <Alert kind={notice.kind}>{notice.text}</Alert>
        </div>
      )}
      {!isOwner && (
        <div className="mb-6">
          <Alert kind="info">Only the account owner can change subscriptions.</Alert>
        </div>
      )}

      <Card title="Your subscriptions" actions={live.length > 0 && <span className="text-xs text-text-secondary">${monthly.toLocaleString()} / month</span>} padded={false} className="mb-8">
        {loading && !subs ? (
          <Loading />
        ) : error ? (
          <LoadError message={error} onRetry={reload} />
        ) : (subs ?? []).length === 0 ? (
          <EmptyState icon={CreditCard} title="No subscriptions yet" body="Choose a plan below. Fleet health monitoring is free and needs no plan." />
        ) : (
          <Table head={['Plan', 'Status', 'Appliances', 'Monthly', 'Renews / ends', '']}>
            {(subs ?? []).map((s) => {
              const plan = findCloudPlan(s.planId);
              const perAppliance = plan?.unit === 'appliance';
              const isLive = LIVE.has(s.status);
              return (
                <tr key={s.id}>
                  <td className="px-5 py-3">
                    <div className="font-semibold">{plan?.name ?? s.planId}</div>
                    <div className="text-xs text-text-muted">{s.provider === 'stripe' ? 'Card (Stripe)' : 'Invoice'}</div>
                  </td>
                  <td className="px-5 py-3">
                    <SubscriptionBadge status={s.status} />
                    {s.cancelAtPeriodEnd && isLive && <div className="text-xs text-status-idle mt-0.5">Cancels at period end</div>}
                  </td>
                  <td className="px-5 py-3">
                    {perAppliance && isLive && isOwner && !s.cancelAtPeriodEnd ? (
                      <QuantityInput value={s.quantity} disabled={busy} onChange={(n) => void changeQuantity(s, n)} />
                    ) : perAppliance ? (
                      s.quantity
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-5 py-3 tabular-nums">${((plan?.priceMonthlyUsd ?? 0) * s.quantity).toLocaleString()}</td>
                  <td className="px-5 py-3 text-text-secondary">{formatDate(s.currentPeriodEnd)}</td>
                  <td className="px-5 py-3 text-right">
                    {isLive && isOwner && !s.cancelAtPeriodEnd && (
                      <button type="button" disabled={busy} onClick={() => void cancel(s)} className="text-xs font-semibold text-status-fault hover:underline disabled:opacity-50">
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>

      <h2 className="text-lg font-bold mb-1">Available plans</h2>
      <p className="text-sm text-text-secondary mb-6">Per-appliance plans cover as many appliances as you choose — you pick which ones on each appliance’s page.</p>
      <div className="space-y-8">
        {CLOUD_PLAN_CATEGORIES.map((cat) => (
          <div key={cat.id}>
            <div className="mb-3">
              <h3 className="text-sm font-semibold">{cat.name}</h3>
              <p className="text-xs text-text-muted">{cat.summary}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {CLOUD_PLANS.filter((p) => p.category === cat.id).map((p) => (
                <div key={p.id} id={`plan-${p.id}`} className="scroll-mt-24">
                  <PlanOption plan={p} subscribed={subscribedIds.has(p.id)} preselected={preselect?.id === p.id} onSubscribe={(plan, q) => void subscribe(plan, q)} busy={busy || !isOwner} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
